import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel
} from '@tanstack/react-table';
import classnames from 'classnames';

import Card from '../../../atoms/Card/Card.react';
import LoadingSpinnerLong from '../../../molecules/LoadingSpinnerLong/LoadingSpinnerLong.react';
import './data-table.scss';

import DataTableHeader from './DataTableHeader';
import DataTableBody from './DataTableBody';
import DataTableContext from './DataTableContext';
import type {DataTableProps} from './DataTable.types';

/**
 * # DataTable
 *
 * A TanStack Table-powered data table using `data-table__*` visual styles.
 * Accepts plain JS arrays and column definitions instead of Immutable.js and JSX children.
 *
 * ## Basic Usage
 *
 * ```tsx
 * import {DataTable} from '@albert-io/atomic';
 * import {createColumnHelper} from '@tanstack/react-table';
 *
 * const col = createColumnHelper<Person>();
 * const columns = [
 *   col.accessor('name', {header: 'Name'}),
 *   col.accessor('email', {header: 'Email'}),
 * ];
 *
 * <DataTable columns={columns} data={people} />
 * ```
 *
 * ## Column Meta Options
 *
 * Column definitions accept a `meta` object for per-column behavior:
 *
 * | Meta Key        | Type                             | Description                                          |
 * |-----------------|----------------------------------|------------------------------------------------------|
 * | `primary`       | `boolean`                        | Renders cell text in bold.                           |
 * | `contentType`   | `'text' \| 'graphic'`            | `'text'` (default) wraps in `<Text>`. `'graphic'` renders raw (for badges, icons, etc.). |
 * | `align`         | `'left' \| 'center' \| 'right'`  | Horizontal text alignment on header and cell.        |
 * | `isActionMenu`  | `boolean`                        | Styles the column as a compact action menu column. Prevents row click from firing. |
 * | `noClamp`       | `boolean`                        | Disables the default 2-line text clamping.           |
 * | `subValue`      | `(row: TData) => string`         | Renders secondary text below the cell value.         |
 * | `className`     | `string`                         | Additional CSS class(es) applied to both `<th>` and `<td>` for custom styling. |
 *
 * You can also use TanStack's `minSize` on a column definition to enforce a
 * minimum column width (useful for columns containing Dropdown triggers or Badges):
 *
 * ```tsx
 * col.accessor('status', {
 *   header: 'Status',
 *   meta: {contentType: 'graphic'},
 *   minSize: 200,
 *   cell: (info) => <StatusBadge status={info.getValue()} />,
 * })
 * ```
 *
 * ## Sorting
 *
 * Client-side sorting is automatic when column headers are clickable.
 * For controlled sorting, pass `sorting` and `onSortingChange`:
 *
 * ```tsx
 * const [sorting, setSorting] = useState<SortingState>([{id: 'name', desc: false}]);
 *
 * <DataTable
 *   columns={columns}
 *   data={people}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 * />
 * ```
 *
 * For server-side sorting, add `manualSorting` and sort `data` yourself
 * before passing it in.
 *
 * ## Pagination
 *
 * Client-side pagination is built in. Pass `pagination`, `onPaginationChange`,
 * and a `tableFooter` with a pagination UI:
 *
 * ```tsx
 * import {DataTable, Pagination} from '@albert-io/atomic';
 *
 * const [pagination, setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 10});
 * const totalPages = Math.ceil(data.length / pagination.pageSize);
 *
 * <DataTable
 *   columns={columns}
 *   data={data}
 *   pagination={pagination}
 *   onPaginationChange={setPagination}
 *   tableFooter={
 *     totalPages > 1 && (
 *       <Pagination
 *         totalPages={totalPages}
 *         defaultPage={pagination.pageIndex + 1}
 *         onChange={(page) => setPagination((prev) => ({...prev, pageIndex: page - 1}))}
 *       />
 *     )
 *   }
 *   tableFooterAlign="right"
 * />
 * ```
 *
 * When paginated, the table locks its height to the first full page so that
 * navigating to a page with fewer rows doesn't cause a layout shift. A partial
 * last page automatically shows a bottom border on the last row.
 *
 * For server-side pagination, set `manualPagination` and `pageCount`.
 *
 * ## Row Selection
 *
 * Enable checkbox selection with `enableRowSelection`. Use controlled state
 * to track which rows are selected:
 *
 * ```tsx
 * const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
 *
 * <DataTable
 *   columns={columns}
 *   data={people}
 *   enableRowSelection
 *   rowSelection={rowSelection}
 *   onRowSelectionChange={setRowSelection}
 * />
 * ```
 *
 * Pass a function to `enableRowSelection` to conditionally disable selection
 * per row: `enableRowSelection={(row) => row.status !== 'locked'}`.
 *
 * ## Row Interaction
 *
 * Make rows clickable or navigable links:
 *
 * ```tsx
 * // Click handler
 * <DataTable columns={columns} data={people} onRowClick={(row) => openDetail(row.id)} />
 *
 * // Link rows (renders as navigable)
 * <DataTable columns={columns} data={people} rowLinkFunc={(row) => `/people/${row.id}`} />
 * ```
 *
 * ## Props Reference
 *
 * | Prop                  | Type                                          | Default     | Description                                                        |
 * |-----------------------|-----------------------------------------------|-------------|--------------------------------------------------------------------|
 * | `columns`             | `ColumnDef<TData, any>[]`                     | *required*  | TanStack column definitions.                                       |
 * | `data`                | `TData[]`                                     | *required*  | Plain JS array of row objects.                                     |
 * | `theme`               | `'data' \| 'list'`                            | `'data'`    | Visual theme — bordered or borderless rows.                        |
 * | `className`           | `string`                                      | —           | Extra CSS class on the `<table>` element.                          |
 * | `headerless`          | `boolean`                                     | `false`     | Hides `<thead>` entirely.                                          |
 * | `lockTableHeight`     | `boolean`                                     | `false`     | Locks the table wrapper height to prevent layout shift on pagination. |
 * | `isLoading`           | `boolean`                                     | `false`     | Shows a full-table spinner (initial load).                         |
 * | `isReloading`         | `boolean`                                     | `false`     | Shows an overlay spinner (background refetch).                     |
 * | `emptyState`          | `ReactNode`                                   | —           | Custom empty content. Defaults to `<EmptyTable />`.                |
 * | `sorting`             | `SortingState`                                | —           | Controlled sort state.                                             |
 * | `onSortingChange`     | `OnChangeFn<SortingState>`                    | —           | Callback when sort changes.                                        |
 * | `manualSorting`       | `boolean`                                     | `false`     | Disables client-side sorting.                                      |
 * | `pagination`          | `PaginationState`                             | —           | Controlled pagination state `{pageIndex, pageSize}`.               |
 * | `onPaginationChange`  | `OnChangeFn<PaginationState>`                 | —           | Callback when pagination changes.                                  |
 * | `pageCount`           | `number`                                      | —           | Total pages for server-side pagination. `-1` if unknown.           |
 * | `manualPagination`    | `boolean`                                     | `false`     | Disables client-side pagination.                                   |
 * | `rowSelection`        | `RowSelectionState`                           | —           | Controlled selection state `{[rowId]: true}`.                      |
 * | `onRowSelectionChange`| `OnChangeFn<RowSelectionState>`               | —           | Callback when selection changes.                                   |
 * | `enableRowSelection`  | `boolean \| (row: TData) => boolean`          | `false`     | Enables checkbox column. Function form for per-row control.        |
 * | `onRowClick`          | `(row: TData) => void`                        | —           | Click handler per row.                                             |
 * | `rowLinkFunc`         | `(row: TData) => string`                      | —           | Makes rows navigable links via the returned href.                  |
 * | `tableFooter`         | `ReactNode`                                   | —           | Content rendered in `<tfoot>` (e.g. `<Pagination />`).             |
 * | `tableFooterAlign`    | `'center' \| 'right'`                         | `'right'`   | Horizontal alignment of the footer content.                        |
 * | `onTableReady`        | `(table: TanStackTable<TData>) => void`       | —           | Exposes the TanStack table instance for external toolbar wiring.   |
 */
function DataTable<TData>({
  columns,
  data,
  theme = 'data',
  className,
  headerless = false,
  lockTableHeight = false,
  isLoading = false,
  isReloading = false,
  emptyState,
  sorting,
  onSortingChange,
  manualSorting = false,
  pagination,
  onPaginationChange,
  pageCount,
  manualPagination = false,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  onRowClick,
  rowLinkFunc,
  tableFooter,
  tableFooterAlign = 'right',
  onTableReady,
  disableRowHover = false
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Sorting
    ...(manualSorting ? {manualSorting: true} : {getSortedRowModel: getSortedRowModel()}),
    // Pagination
    ...(manualPagination
      ? {manualPagination: true}
      : {getPaginationRowModel: getPaginationRowModel()}),
    // Build state object from controlled props
    ...((sorting !== undefined || pagination !== undefined || rowSelection !== undefined) && {
      state: {
        ...(sorting !== undefined && {sorting}),
        ...(pagination !== undefined && {pagination}),
        ...(rowSelection !== undefined && {rowSelection})
      }
    }),
    ...(onSortingChange && {onSortingChange}),
    // Pagination
    ...(pageCount !== undefined && {pageCount}),
    ...(onPaginationChange && {onPaginationChange}),
    // Row selection
    enableRowSelection:
      typeof enableRowSelection === 'function'
        ? (row) => enableRowSelection(row.original)
        : enableRowSelection,
    ...(onRowSelectionChange && {onRowSelectionChange})
  });

  // Expose table instance to parent via callback ref pattern.
  // This runs synchronously during render so callers can access
  // the table instance immediately (e.g. for toolbar wiring).
  if (onTableReady) {
    onTableReady(table);
  }

  const visibleColumnCount = table.getVisibleFlatColumns().length + (enableRowSelection ? 1 : 0);
  const rowCount = table.getRowModel().rows.length;

  // ---------------------------------------------------------------------------
  // Lock table height – prevents layout shift when paginating or reloading.
  //
  const lockedMinHeight = useRef<number>(0);
  const wrapperNodeRef = useRef<HTMLDivElement | null>(null);

  const wrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperNodeRef.current = node;
      if (!node) return;
      if (!lockTableHeight && lockedMinHeight.current > 0) {
        lockedMinHeight.current = 0;
        node.style.minHeight = '';
      }
    },
    [lockTableHeight]
  );

  // The effect below re-measures whenever the row count or loading state
  // changes, and deliberately skips measurement during loading/reloading so we
  // never lock to the spinner's height. This ensures the locked minHeight
  // always reflects a full page of actual data rows.
  useEffect(() => {
    const node = wrapperNodeRef.current;
    if (!node || !lockTableHeight) return;

    // Don't capture height during loading/reloading — the table only contains
    // a spinner row and would lock to a misleadingly small value.
    if (isLoading || isReloading) return;

    if (node.offsetHeight > lockedMinHeight.current) {
      lockedMinHeight.current = node.offsetHeight;
      node.style.minHeight = `${lockedMinHeight.current}px`;
    }
  }, [lockTableHeight, isLoading, isReloading, rowCount]);

  // End of lock table height logic
  // ---------------------------------------------------------------------------

  const {pageSize} = table.getState().pagination;
  const isPartialPage = pagination !== undefined && rowCount > 0 && rowCount < pageSize;

  const contextValue = useMemo(
    () => ({
      onRowClick,
      rowLinkFunc,
      enableRowSelection,
      disableRowHover
    }),
    [onRowClick, rowLinkFunc, enableRowSelection, disableRowHover]
  );

  return (
    <DataTableContext.Provider value={contextValue}>
      <Card
        ref={wrapperRef}
        className='data-table__wrapper relative flex flex-col overflow-x-auto'
        border={theme === 'list' ? 'none' : 'regular'}
      >
        <table
          className={classnames('data-table flex-1', className, {
            [`data-table--${theme}`]: theme,
            'data-table--partial-page': isPartialPage
          })}
        >
          {!headerless && <DataTableHeader table={table} enableRowSelection={enableRowSelection} />}
          <DataTableBody
            table={table}
            isLoading={isLoading}
            emptyState={emptyState}
            columnCount={visibleColumnCount}
          />
          {tableFooter && (
            <tfoot>
              <tr role='presentation'>
                <td className='data-table__footer-cell' colSpan={visibleColumnCount}>
                  <div
                    className={classnames('flex p-x2', {
                      'justify-center': tableFooterAlign === 'center',
                      'justify-end': tableFooterAlign === 'right'
                    })}
                  >
                    {tableFooter}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
        {isReloading && !isLoading && (
          <div
            className='data-table__reloading-overlay'
            role='status'
            aria-live='polite'
            aria-label='Reloading'
          >
            <LoadingSpinnerLong />
          </div>
        )}
      </Card>
    </DataTableContext.Provider>
  );
}

export default DataTable;
